import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useCallback } from 'react';

import { packages } from "../../../env";
import Button from "../../common/Button";
import { getRequest, postRequest } from '../../../config';
import ModalWrapper from '../../common/Modal/ModalWrapper';
import { toggleModal } from '../../../reducers/modalReducer';

import debounce from 'lodash/debounce';
import { stepConfig } from './constants';
import PurchaseInfo from './PurchaseInfo';
import ActionButtons from './ActionButtons';
import PromoCodeInput from './PromoCodeInput';

const SubscriptionPaymentConfirm = ({ plan, companyId, defaultStep, setShowExpension, payload, setModuleTab, module, navigateTo, onCancel }) => {
    const dispatch = useDispatch();
    const { open } = useSelector(state => state.modal);

    const [loading, setLoading] = useState(false);
    const [promoCode, setPromoCode] = useState("");
    const [promoCodeData, setPromoCodeData] = useState(null);
    const [isValidCoupon, setIsValidCoupon] = useState(true);
    const [isCheckingCoupon, setIsCheckingCoupon] = useState(false);
    const [step, setStep] = useState(defaultStep ?? "subscriptionUpgrade");

    const checkCoupon = useCallback(
        debounce(async (code) => {
            if (!code) {
                setIsValidCoupon(true);
                setIsCheckingCoupon(false);
                return;
            }

            try {
                const response = await postRequest(`/payment/checkCoupon/${code}`, { amount: packages[plan]?.price });
                setPromoCodeData(response?.body);
                setIsValidCoupon(response?.code === 200);
            } catch (error) {
                setIsValidCoupon(false);
            } finally {
                setIsCheckingCoupon(false);
            }
        }, 1000),
        [plan]
    );

    useEffect(() => {
        setIsCheckingCoupon(true);
        checkCoupon(promoCode);
    }, [promoCode, checkCoupon]);

    useEffect(() => {
        if (defaultStep) {
            setStep(defaultStep);
        } else {
            if (plan === "Free") {
                setStep("freeMembership");
            } else if (plan === "modulePurchase") {
                setStep("modulePurchase");
            } else if (plan.startsWith('two-minute-') || plan.startsWith('investor-lead')) {
                setStep("freeModule");
            } else {
                setStep("subscriptionUpgrade");
            }
        }
    }, [plan, open]);

    const handleClose = () => {
        setPromoCodeData(null);
        setPromoCode("");
        dispatch(toggleModal({ open: '' }));
        setStep("subscriptionUpgrade");
        window.history.replaceState(null, null, '');
        if (setShowExpension) {
            setShowExpension(null)
        }
        if (onCancel) onCancel()
    };

    const handlePurchase = async () => {
        console.log("********module:::", module);

        setLoading(true);
        if (plan === "Free") {
            const response = await getRequest(`/payment/freeSubscription/${companyId}`);
            if (response?.code === 200) {
                if (navigateTo) {
                    dispatch(toggleModal({ open: '' }));
                } else {
                    setTimeout(() => location.href = `/company/${companyId}`, 1000);
                }
            }
        } else {
            const data = {
                endPoint: `/payment/upgrade-subscription/${companyId}`,
                payload: { packageName: plan, ...(promoCodeData && { promoCode: promoCodeData.stripePromotionCodeId }) }
            };

            if (['Free', 'Lite', 'Pro'].includes(plan) && promoCodeData && promoCodeData?.discountType === "percentage" && promoCodeData?.discountValue === 100) {
                data.endPoint = `/payment/subscriptionWithAccessCode/${companyId}`;
                data.payload = { packageName: plan, promoCode: promoCodeData.code };
            }

            if (defaultStep === "modulePurchase") {
                data.endPoint = `/payment/purchase-expansion/${companyId}`;
                data.payload = { expansionName: plan, ...(promoCodeData && { promoCode: promoCodeData.stripePromotionCodeId }) }

                if (promoCodeData && promoCodeData?.discountType === "percentage" && promoCodeData?.discountValue === 100) {
                    data.endPoint = `/payment/addExpensionPackWithAccessCode/${companyId}`;
                    data.payload = { expansionName: plan, promoCode: promoCodeData.code };
                }
            }

            const response = await postRequest(`${data?.endPoint}`, data?.payload);
            if (response?.code === 200) {
                window.location.href = response.body?.url ?? `/company/${companyId}`;
                // if (module) {
                //     // if (module == 'DEPF006') window.location.href = `/company/${companyId}#warranty-disclosure`;
                //     // else if (module?.['isHealthBoost']) window.location.href = `/company/${companyId}#${module['type']}`;
                //     window.location.reload(true);
                // }
                // else window.location.href = response.body?.url ?? `/company/${companyId}`;
            }
        }
        if (setShowExpension) {
            setShowExpension(null);
        }
        dispatch(toggleModal({ open: '' }));
        setPromoCode("");
        setLoading(false);
    };

    const { title, subtitle } = stepConfig[step] ?? {};
    return (
        <ModalWrapper onCloseHandler={handleClose} modalType='subscriptionPaymentConfirm'>
            <div className='df fdc jcc aic'>
                <img
                    style={{ width: '93px', height: '75px' }}
                    src={require('../../../../images/checklist-logo.webp')}
                    alt='logoDiligentsia'
                />

                <h3 className='c15 tac mt1rem fw400 fs22px'>{title}</h3>

                {navigateTo && step === "discountCode" ? null : <p className='c15 op60 fs10px my0-5rem tac fw500'>{subtitle}</p>}


                {step === "subscriptionUpgrade" && (
                    <>
                        <PurchaseInfo text={<>You are buying the {plan} subscription at<br />£{Number(packages[plan]?.price / 12).toFixed(2)} <span class='fs0-75rem c14 fw300'>(plus VAT)</span> per month for 12 months</>} />
                        <ActionButtons disabled={loading} plan={plan} onCancel={handleClose} onConfirm={() => setStep("discountCode")} />
                    </>
                )}

                {step === "discountCode" && (
                    <>
                        <PromoCodeInput
                            promoCode={promoCode}
                            setPromoCode={setPromoCode}
                            isValid={isValidCoupon}
                            isChecking={isCheckingCoupon}
                        />
                        <div className='df fdc aic jcc w100 mt3rem mb1rem' style={{ width: '280px' }}>
                            <Button
                                onClick={handlePurchase}
                                btnType="warning"
                                customClassName='w100'
                                text="Continue to Purchase"
                                disabled={loading || isCheckingCoupon || (!isValidCoupon && promoCode !== '')}
                            />
                        </div>
                    </>
                )}

                {step === "freeMembership" && (
                    <>
                        <PurchaseInfo text={
                            <>The <span className='c59 op70 ttu'>{plan}</span> subscription offers a great deal more and is <br /> only £{packages[plan]?.price} per year. Later you can upgrade to Lite or Pro from within your dashboard.</>
                        } />
                        <ActionButtons disabled={loading} plan={plan} step={step} onCancel={handleClose} onConfirm={handlePurchase} confirmText="Subscribe for Free" />
                    </>
                )}

                {step === "modulePurchase" ? (
                    <>
                        <PurchaseInfo text={
                            <>The module is {module && typeof module !== "string" ? module['type'] : packages[plan]?.name} <br />The module price is {module && typeof module !== "string" ? module['price'] : `£${packages[plan]?.price}`} <span class='fs0-75rem c14 fw300'>(plus VAT)</span></>
                        } />
                        <ActionButtons disabled={loading} plan={defaultStep} onCancel={handleClose} onConfirm={() => setStep("discountCode")} />
                    </>
                ) : <></>}

                {step === "freeModule" && (
                    <>
                        <PurchaseInfo text={
                            <>The module is {payload.module.heading} <br />This module is free to use</>
                        } />
                        <ActionButtons
                            disabled={loading}
                            plan={plan}
                            onCancel={handleClose}
                            onConfirm={() => {
                                setModuleTab && setModuleTab(payload.setTab, payload.type, payload.module);
                            }}
                            confirmText="Start Module"
                        />
                    </>
                )}
            </div>
        </ModalWrapper>
    );
};

export default SubscriptionPaymentConfirm;
